import { useCallback, useReducer, useContext, useEffect } from 'react';
import axios from 'axios';
import { ActionDispatchContext } from 'lib/ActionDispatchContext';

import {
  ActionType,
  requestActionCreator,
  successActionCreator,
  failureActionCreator,
} from '../actionCreators';
import { useToastError } from './useToastError';

export interface UploadData {
  url: string;
  file: File;
  contentType?: string;
  onUploadProgress: (event: ProgressEvent, id?: number | string) => void;
  id?: number;
}

interface UseS3Upload {
  data: any;
  loading: boolean;
  error: string | null;
  uploadVideo(video: UploadData): void;
}

interface MutationState {
  error: string | null;
  loading: boolean;
  data: any;
}

function reducer(state: MutationState, action: ActionType) {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        data: action.payload,
        loading: false,
        error: null,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    default:
      return state;
  }
}

const useActionListener = (dispatch: Function) => {
  const { action } = useContext(ActionDispatchContext);

  useEffect(() => {
    dispatch(action);
  }, [action, dispatch]);
};

const initialState = {
  data: undefined,
  loading: false,
  error: null,
};

export const useS3Upload = (): UseS3Upload => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { error, loading, data } = state;
  const { showError } = useToastError();

  const checkIfFirefox = () => {
    return navigator?.userAgent?.indexOf('Firefox') !== -1;
  };

  useActionListener(dispatch);

  const uploadVideo = useCallback(async (uploadData: UploadData) => {
    dispatch(
      requestActionCreator({
        type: 'FETCH_REQUEST',
      })
    );
    const onUploadProgress = (progressEvent: any) => {
      uploadData.onUploadProgress(
        progressEvent,
        uploadData.hasOwnProperty('id') ? uploadData.id : undefined
      );
    };
    try {
      const isFirefox = checkIfFirefox();
      await axios.put(uploadData.url, uploadData.file, {
        headers: {
          'content-type':
            !isFirefox && uploadData.contentType
              ? uploadData.contentType
              : uploadData.file.type,
        },
        onUploadProgress: onUploadProgress,
      });

      dispatch(
        successActionCreator({
          type: 'FETCH_SUCCESS',
          payload: {
            success: true,
            id: uploadData.hasOwnProperty('id') ? uploadData.id : undefined,
          },
        })
      );
      //const response: UploadVideoResponse = data;
      //toast({ message: response });

      return;
    } catch (err) {
      if (err instanceof Error) {
        showError(err);
        // Axios automatically throws an error in case of HTTP or Network errors
        // so we can eventually put error parsing here and return whatever value we want based on that.
        dispatch(
          failureActionCreator({
            type: 'FETCH_FAILURE',
            payload: 'Something wen wrong',
          })
        );

        throw new Error(err.message);
      }
    }
  }, []);

  return {
    data,
    loading,
    error,
    uploadVideo,
  };
};
